import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Users, 
  CheckCircle2, 
  AlertTriangle,
  Play, 
  ArrowRight,
  Plus,
  Phone,
  Navigation,
  TrendingUp,
  Zap,
  Eye,
  UserCheck,
  Timer,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OperationsDashboardPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [todayBookings, setTodayBookings] = useState([]);
  const [weekBookings, setWeekBookings] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  const api = axios.create({
    baseURL: API_URL,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  useEffect(() => {
    if (token) fetchDashboardData();
  }, [token]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, todayRes, weekRes, staffRes] = await Promise.all([
        api.get('/api/bookings/dashboard-stats'),
        api.get('/api/bookings/today'),
        api.get('/api/bookings/week'),
        api.get('/api/users?role=staff').catch(() => ({ data: { users: [] } }))
      ]);
      setStats(statsRes.data);
      setTodayBookings(todayRes.data.bookings || []);
      setWeekBookings(weekRes.data.bookings || []);
      setStaff(staffRes.data?.users || staffRes.data || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      completed: { color: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', label: 'Completed', icon: CheckCircle2 },
      started: { color: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', label: 'In Progress', icon: Play },
      in_progress: { color: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', label: 'In Progress', icon: Play },
      confirmed: { color: 'bg-primary', text: 'text-primary', bg: 'bg-teal-50', label: 'Confirmed', icon: CheckCircle2 },
      scheduled: { color: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50', label: 'Scheduled', icon: Calendar },
      draft: { color: 'bg-gray-400', text: 'text-gray-600', bg: 'bg-gray-50', label: 'Draft', icon: Clock },
    };
    return configs[status] || configs.draft;
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  // Filter bookings based on active filter
  const filteredBookings = todayBookings.filter(b => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'in_progress') return ['started', 'in_progress'].includes(b.status);
    if (activeFilter === 'pending') return ['draft', 'scheduled', 'confirmed'].includes(b.status);
    if (activeFilter === 'completed') return b.status === 'completed';
    return true;
  });

  // Get staff assignments for today
  const getStaffAssignments = (staffId) => {
    return todayBookings.filter(b => 
      b.assigned_staff_id === staffId || 
      (b.assigned_staff_ids || []).includes(staffId)
    );
  };

  // Calculate completion percentage
  const completionPercent = stats?.today?.total > 0 
    ? Math.round((stats.today.completed / stats.today.total) * 100) 
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="operations-dashboard">
      {/* Header with Quick Actions */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 18 ? 'Afternoon' : 'Evening'}!</h1>
          <p className="text-muted-foreground">
            {new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/calendar')} data-testid="view-calendar-btn">
            <Calendar className="h-4 w-4 mr-2" />
            Calendar
          </Button>
          <Button onClick={() => navigate('/bookings/new')} data-testid="new-booking-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Booking
          </Button>
        </div>
      </div>

      {/* Stats Row - Clickable Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${activeFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setActiveFilter('all')}
          data-testid="stat-total"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Today's Jobs</p>
                <p className="text-3xl font-bold text-foreground">{stats?.today?.total || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-primary" />
              </div>
            </div>
            <Progress value={completionPercent} className="mt-3 h-2" />
            <p className="text-xs text-muted-foreground mt-1">{completionPercent}% complete</p>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${activeFilter === 'in_progress' ? 'ring-2 ring-amber-500' : ''}`}
          onClick={() => setActiveFilter('in_progress')}
          data-testid="stat-progress"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">In Progress</p>
                <p className="text-3xl font-bold text-amber-600">{stats?.today?.in_progress || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Play className="h-6 w-6 text-amber-600" />
              </div>
            </div>
            {stats?.today?.in_progress > 0 && (
              <p className="text-xs text-amber-600 mt-3 flex items-center gap-1">
                <Zap className="h-3 w-3" /> Active now
              </p>
            )}
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${activeFilter === 'completed' ? 'ring-2 ring-emerald-500' : ''}`}
          onClick={() => setActiveFilter('completed')}
          data-testid="stat-completed"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Completed</p>
                <p className="text-3xl font-bold text-emerald-600">{stats?.today?.completed || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
            <p className="text-xs text-emerald-600 mt-3 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" /> {stats?.week?.completed || 0} this week
            </p>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${activeFilter === 'pending' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => setActiveFilter('pending')}
          data-testid="stat-pending"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending</p>
                <p className="text-3xl font-bold text-blue-600">{stats?.today?.pending || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Timer className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-blue-600 mt-3">Awaiting start</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Jobs List - Takes 2 columns */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              {activeFilter === 'all' ? "Today's Schedule" : 
               activeFilter === 'in_progress' ? 'In Progress' :
               activeFilter === 'completed' ? 'Completed Jobs' : 'Pending Jobs'}
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({filteredBookings.length})
              </span>
            </h2>
            {activeFilter !== 'all' && (
              <Button variant="ghost" size="sm" onClick={() => setActiveFilter('all')}>
                Show all
              </Button>
            )}
          </div>

          {filteredBookings.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">
                  {activeFilter === 'all' ? 'No Jobs Today' : `No ${activeFilter.replace('_', ' ')} jobs`}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {activeFilter === 'all' 
                    ? "You don't have any jobs scheduled for today."
                    : 'Try selecting a different filter above.'}
                </p>
                <Button onClick={() => navigate('/bookings/new')} variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Create a Booking
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredBookings.map((booking) => {
                const statusConfig = getStatusConfig(booking.status);
                const StatusIcon = statusConfig.icon;
                
                return (
                  <Card 
                    key={booking.id}
                    className={`cursor-pointer transition-all hover:shadow-md group ${statusConfig.bg} border-l-4`}
                    style={{ borderLeftColor: statusConfig.color.includes('emerald') ? '#10b981' : 
                             statusConfig.color.includes('amber') ? '#f59e0b' : 
                             statusConfig.color.includes('blue') ? '#3b82f6' : '#0891b2' }}
                    onClick={() => navigate(`/bookings/${booking.id}`)}
                    data-testid={`booking-card-${booking.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-foreground truncate text-lg">
                              {booking.client_name || 'Unknown Client'}
                            </h3>
                            <Badge className={`${statusConfig.color} text-white text-xs shrink-0`}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                            {booking.time_window_start && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Clock className="h-4 w-4 shrink-0" />
                                <span>{booking.time_window_start}{booking.time_window_end && ` - ${booking.time_window_end}`}</span>
                              </div>
                            )}
                            
                            {booking.address && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <MapPin className="h-4 w-4 shrink-0" />
                                <span className="truncate">{booking.address.split(',')[0]}</span>
                              </div>
                            )}
                            
                            {(booking.assigned_staff_name || booking.assigned_staff_names?.length > 0) && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Users className="h-4 w-4 shrink-0" />
                                <span className="truncate">{booking.assigned_staff_name || booking.assigned_staff_names?.join(', ')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 shrink-0">
                          {booking.address && (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`https://maps.google.com/?q=${encodeURIComponent(booking.address)}`, '_blank');
                              }}
                              data-testid={`navigate-${booking.id}`}
                            >
                              <Navigation className="h-4 w-4" />
                            </Button>
                          )}
                          <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Sidebar - Staff & Quick Links */}
        <div className="space-y-4">
          {/* Staff On Duty */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <UserCheck className="h-5 w-5 text-primary" />
                  Team Today
                </h3>
                <Button variant="ghost" size="sm" onClick={() => navigate('/admin?tab=staff')}>
                  Manage
                </Button>
              </div>
              
              {staff.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No staff members found</p>
              ) : (
                <div className="space-y-3">
                  {staff.slice(0, 5).map((member) => {
                    const assignments = getStaffAssignments(member.id);
                    const completedCount = assignments.filter(a => a.status === 'completed').length;
                    const activeJob = assignments.find(a => ['started', 'in_progress'].includes(a.status));
                    
                    return (
                      <div 
                        key={member.id} 
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/bookings?staff=${member.id}`)}
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className={`${activeJob ? 'bg-amber-100 text-amber-700' : 'bg-primary/10 text-primary'}`}>
                            {member.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{member.full_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {activeJob ? (
                              <span className="text-amber-600 flex items-center gap-1">
                                <Play className="h-3 w-3" /> {activeJob.client_name}
                              </span>
                            ) : assignments.length > 0 ? (
                              `${completedCount}/${assignments.length} jobs done`
                            ) : (
                              'No assignments'
                            )}
                          </p>
                        </div>
                        {member.phone && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`tel:${member.phone}`, '_self');
                            }}
                          >
                            <Phone className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Alerts/Needs Attention */}
          {(stats?.today?.pending > 0 || todayBookings.some(b => !b.assigned_staff_id && !b.assigned_staff_ids?.length)) && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <h3 className="font-semibold flex items-center gap-2 text-amber-800 mb-3">
                  <AlertTriangle className="h-5 w-5" />
                  Needs Attention
                </h3>
                <div className="space-y-2">
                  {todayBookings.filter(b => !b.assigned_staff_id && !b.assigned_staff_ids?.length).slice(0, 3).map(booking => (
                    <div 
                      key={booking.id}
                      className="flex items-center justify-between p-2 bg-white rounded-lg cursor-pointer hover:bg-amber-100 transition-colors"
                      onClick={() => navigate(`/bookings/${booking.id}`)}
                    >
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-amber-600" />
                        <span className="text-sm font-medium truncate">{booking.client_name}</span>
                      </div>
                      <Badge variant="outline" className="text-amber-700 border-amber-300">
                        Unassigned
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Links */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-3">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  variant="outline" 
                  className="h-auto py-3 flex-col gap-1"
                  onClick={() => navigate('/bookings')}
                >
                  <Eye className="h-5 w-5" />
                  <span className="text-xs">All Bookings</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-auto py-3 flex-col gap-1"
                  onClick={() => navigate('/clients')}
                >
                  <Users className="h-5 w-5" />
                  <span className="text-xs">Clients</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-auto py-3 flex-col gap-1"
                  onClick={() => navigate('/calendar')}
                >
                  <Calendar className="h-5 w-5" />
                  <span className="text-xs">Calendar</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-auto py-3 flex-col gap-1"
                  onClick={() => navigate('/bookings/new')}
                >
                  <Plus className="h-5 w-5" />
                  <span className="text-xs">New Job</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Week Preview */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">This Week</h3>
                <Button variant="ghost" size="sm" onClick={() => navigate('/calendar')}>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Total Jobs</span>
                  <span className="font-semibold">{stats?.week?.total || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Completed</span>
                  <span className="font-semibold text-emerald-600">{stats?.week?.completed || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Remaining</span>
                  <span className="font-semibold text-blue-600">{(stats?.week?.total || 0) - (stats?.week?.completed || 0)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default OperationsDashboardPage;
